import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { Get } from "../config/api/get";
import { Post } from "../config/api/post";
import { useSelector } from "react-redux";
import { TEACHERS, CLASSROOM, CONTENT_TYPE } from "../config/constants";
import swal from "../utils/swal";

const TEACHERS_QUERY_KEY = ["get-teachers"];

const invalidateTeachersList = (queryClient) => {
  queryClient.invalidateQueries({ queryKey: TEACHERS_QUERY_KEY });
};

export const useGetAllTeachers = (params = {}) => {
  const token = useSelector((state) => state.user.userToken);

  return useQuery({
    queryKey: [...TEACHERS_QUERY_KEY, params],
    queryFn: async ({ signal }) => {
      try {
        const response = await Get(TEACHERS.getAllTeachers, token, params, { signal });
        if (response?.status === false) {
          throw new Error(response?.message || "Failed to fetch teachers");
        }
        return response?.data || { docs: [], totalPages: 1, totalDocs: 0 };
      } catch (error) {
        console.error("Error fetching teachers:", error);
        throw error.data || error;
      }
    },
    enabled: !!token,
    placeholderData: keepPreviousData,
  });
};


export const useUpdateTeacherStatus = () => {
  const queryClient = useQueryClient();
  const token = useSelector((state) => state.user.userToken);

  return useMutation({
    mutationFn: async ({ teacherId, status }) => {
      try {
        const response = await Post(TEACHERS.updateTeacher + teacherId, { status }, token);
        if (response?.data?.status) {
          return response.data; // Success case
        } else {
          throw new Error(response?.data?.message || response?.response?.data?.message || "Failed to create teacher");
        }

      } catch (error) {
        console.error("Error toggling status:", error);
        throw error.data;
      }
    },
    onSuccess: (_data, variables) => {
      invalidateTeachersList(queryClient);
      if (variables?.teacherId) {
        queryClient.invalidateQueries({ queryKey: ["teacher", variables.teacherId] });
      }
    },
  });
};


export const useAddTeacher = () => {
  const queryClient = useQueryClient();
  const token = useSelector((state) => state.user.userToken);

  return useMutation({
    mutationFn: async ({ data }) => {
      try {
        const response = await Post(TEACHERS.addTeacher, data, token, null, CONTENT_TYPE.FORM_DATA);

        if (response?.data?.status) {
          return response.data; // Success case
        } else {
          throw new Error(response?.data?.message || response?.response?.data?.message || "Failed to create teacher");
        }
      } catch (error) {
        console.error("Error adding teacher:", error);
         throw error.data;
      }
    },
    onSuccess: () => {
      invalidateTeachersList(queryClient);
    }
  });
};

export const useSearchClassroom = (params) => {
  const token = useSelector((state) => state.user.userToken);
  return useQuery({
    queryKey: ["search-classrooms", params],
    queryFn: async () => {
      try {
        const { data } = await Get(TEACHERS.searchClassrooms, token, params);
        return data.classrooms || []; // Default response structure
      } catch (error) {
        console.error("Error fetching classrooms:", error);
        throw error.data;
      }
    },
    enabled: !!token,
    placeholderData: keepPreviousData,
  });
};

export const useGetTeacherAttendanceByMonth = (params) => {
  const token = useSelector((state) => state.user.userToken);

  return useQuery({
    queryKey: ["teacher-attendance", params],
    queryFn: async () => {
      try {
        const { data } = await Get(TEACHERS.getTeacherAttendanceByMonth + params.id, token, params);
        return data.attendance || []; // Default response structure
      } catch (error) {
        console.error("Error fetching teachers:", error);
        throw error.data;
      }
    }
  });
}



export const useGetTeacherById = (teacherId) => {
  const token = useSelector((state) => state.user.userToken);

  return useQuery({
    queryKey: ["teacher", teacherId],
    queryFn: async () => {
      try {
        const response = await Get(TEACHERS.getTeacherById + teacherId, token);
        return response.data.teacher;
      } catch (error) {
        console.error("Error fetching teacher:", error);
        throw error.data;
      }
    },
    enabled: !!token && !!teacherId,
  });
}



export const useGetClassrooms = (values) => {
  const token = useSelector((state) => state.user.userToken);

  return useQuery({
    queryKey: "classrooms",
    queryFn: async () => {
      const response = await Get(`${CLASSROOM.getAllClassrooms}`, token);
      return response.data.docs;
    },
  })
};

export const useUpdateTeacher = () => {
  const queryClient = useQueryClient();
  const token = useSelector((state) => state.user.userToken);

  return useMutation({
    mutationFn: async ({ teacherId, data }) => {
            console.log("dataLLLLL", data)

      try {
        const response = await Post(TEACHERS.updateTeacher + teacherId, data, token, null,CONTENT_TYPE.FORM_DATA);
        console.log("data", response)
        if (response?.data?.status) {
          return response.data; // Success case
        } else {
          throw new Error(response?.data?.message || response?.response?.data?.message || "Failed to create teacher");
        }
      } catch (error) {
        console.error("Error updating teacher:", error);
        throw error.data;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["teacher"] });
      invalidateTeachersList(queryClient);
    },
  })
};


export const useDeleteTeacher = () => {
  const queryClient = useQueryClient();
  const token = useSelector((state) => state.user.userToken);

  return useMutation({
    mutationFn: async (teacherId) => {
      const response = await Get(TEACHERS.deleteTeacher + teacherId, token);
      const body =
        typeof response?.status === "boolean"
          ? response
          : response?.response?.data || response?.data;
      if (!body?.status) {
        throw new Error(body?.message || "Failed to delete teacher");
      }
      return body;
    },
    onSuccess: () => {
      invalidateTeachersList(queryClient);
    },
  });
}


export const useMarkAttendance = () => {
  const queryClient = useQueryClient();
  const token = useSelector((state) => state.user.userToken);

  return useMutation({
    mutationFn: async ({ teacherId, data }) => {
      try {
        const response = await Post(TEACHERS.markAttendance + teacherId, data, token);
        return response.data;
      } catch (error) {
        console.error("Error marking attendance:", error);
        throw error.data;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["teacher-attendance"] });
    },
  });
}

export const useUpdateAttendance = () => {
  const queryClient = useQueryClient();
  const token = useSelector((state) => state.user.userToken);

  return useMutation({
    mutationFn: async ({ attendanceId, data }) => {
      try {
        const response = await Post(TEACHERS.updateAttendance + attendanceId, data, token);
        console.log(">>>>>>", response)
        return response.data;
      } catch (error) {
        swal("Error", error.data.message, "error");
        throw error.data;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["teacher"] });
      invalidateTeachersList(queryClient);
      queryClient.invalidateQueries({ queryKey: ["teacher-attendance"] });
    },
  });
}

export const useDeleteAttendance = () => {
  const queryClient = useQueryClient();
  const token = useSelector((state) => state.user.userToken);

  return useMutation({
    mutationFn: async (attendanceId) => {
      try {
        const response = await Post(TEACHERS.deleteAttendance + attendanceId,{}, token);
        return response.data;
      } catch (error) {
        console.error("Error deleting teacher:", error);
        throw error.data;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["teacher-attendance"] });
    },
  });
}